// Estado de inscripción de una categoría (Eduardo 2026-10-24, cierre por
// categoría). Espejo de _division_closed_reason del backend: abierta si el
// torneo no terminó, no ha empezado (el día de inicio solo abre si el cierre
// cae ese día), no se llenó y hoy (fecha de CDMX) <= su cierre (el propio o
// el del torneo; sin ninguno, sin límite de fecha). Excepción (torneo CNA,
// Eduardo 2026-10-27): con cierre PROPIO posterior al inicio, la categoría
// sigue abierta tras el inicio hasta ese cierre. «Cerrar inscripciones ahora»
// (Eduardo 2026-10-30) cierra las que sus fechas dejaban abiertas, llenas o no.
import { shortDateEs } from "./tournamentDivisions";

export type DivisionClosedReason = "closed" | "started" | "deadline" | "organizer" | "full";

export type DivisionRegistrationState = {
  open: boolean;
  reason: DivisionClosedReason | null;
  deadline: string | null;
};

/** Hoy en Ciudad de México como AAAA-MM-DD. */
export function todayMexicoIso(now: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Mexico_City",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(now);
    const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
    return `${get("year")}-${get("month")}-${get("day")}`;
  } catch {
    const shifted = new Date(now.getTime() - 6 * 60 * 60 * 1000);
    return shifted.toISOString().slice(0, 10);
  }
}

export function divisionRegistrationState(args: {
  tournament:
    | {
        status?: string | null;
        start_date?: string | null;
        registration_deadline?: string | null;
        registrations_closed_now?: boolean | null;
      }
    | null
    | undefined;
  division: { registration_deadline?: string | null; max_participants?: number | null };
  approvedCount: number;
  today: string;
}): DivisionRegistrationState {
  const { tournament, division, approvedCount, today } = args;
  const deadline = division.registration_deadline || tournament?.registration_deadline || null;
  if (tournament?.status === "closed" || tournament?.status === "archived") {
    return { open: false, reason: "closed", deadline };
  }
  const start = tournament?.start_date || null;
  const ownAfterStart = Boolean(start && division.registration_deadline && division.registration_deadline > start);
  if (start && !ownAfterStart) {
    const coversStart = Boolean(deadline && deadline >= start);
    if (today > start || (today === start && !coversStart)) return { open: false, reason: "started", deadline };
  }
  if (deadline && today > deadline) return { open: false, reason: "deadline", deadline };
  if (tournament?.registrations_closed_now) return { open: false, reason: "organizer", deadline };
  if (division.max_participants != null && approvedCount >= division.max_participants) {
    return { open: false, reason: "full", deadline };
  }
  return { open: true, reason: null, deadline };
}

/** Chip de Inscripciones: «Abierta hasta 30 nov», «Abierta», «Cupo lleno» o «Cerrada». */
export function divisionStateChipLabel(state: Pick<DivisionRegistrationState, "open" | "reason" | "deadline">): string {
  if (state.open) return state.deadline ? `Abierta hasta ${shortDateEs(state.deadline)}` : "Abierta";
  if (state.reason === "full") return "Cupo lleno";
  return "Cerrada";
}

/** Motivo para el jugador en el link público. */
export function divisionClosedReasonText(reason: string | null | undefined, deadline: string | null | undefined): string {
  if (reason === "full") return "Esta categoría ya llenó su cupo.";
  if (reason === "started") return "El torneo ya comenzó.";
  if (reason === "closed") return "El torneo ya terminó.";
  if (reason === "organizer") return "El organizador cerró las inscripciones.";
  return deadline
    ? `Las inscripciones de esta categoría cerraron el ${shortDateEs(deadline)}.`
    : "Las inscripciones de esta categoría ya cerraron.";
}
